import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { TenantPage } from 'layouts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { equipmentService } from 'shared/services';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { IEquipmentSummary } from '../types';
import { equipmentCheckListTemplateService } from '../api/checklist-template.service';

interface Item {
  id: string; // ID client-side per la gestione delle chiavi React
  description: string;
}

interface Section {
  id: string; // ID client-side per la gestione delle chiavi React
  title: string;
  period: 'none' | 'morning' | 'afternoon';
  items: Item[];
}

export const CreateChecklist = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const location = useLocation();

  const [selectedEquipment, setSelectedEquipment] = useState<Option[]>([]);
  const [equipments, setEquipments] = useState<Option[]>([]);

  const [frequency, setFrequency] = useState<string>('daily');
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [frequencyTypes, setFrequencyTypes] = useState<Option[]>([]);

  const [sections, setSections] = useState<Section[]>([]);

  useEffect(() => {
    loadTypeOptions();
  }, []);

  const loadTypeOptions = () => {
    equipmentCheckListTemplateService.options().then((res) => {
      setFrequencyTypes(
        (res.actions?.POST?.frequency?.choices || []).map((choice: any) => ({
          id: choice.value,
          code: choice.display_name
        }))
      );
    });
  };

  useEffect(() => {
    equipmentService.getAllBaseInformation().then((res) => {
      setEquipments(
        res.map((eq) => {
          return {
            id: eq['id'],
            code: `${eq['description']} (SN ${eq['serial_number']})`
          };
        })
      );
    });
  }, []);

  const resetFormFields = () => {
    setSelectedEquipment(null);
    setFrequency('daily');
    setName('');
    setDescription('');
    setNotes('');
    setSections([]);
  };

  useEffect(() => {
    equipmentService.getAllBaseInformation().then((res) => {
      if (!res) return;

      const mappedEquipments = res.map((eq: any) => ({
        id: eq.id,
        code: `${eq.description || ''} (SN ${eq.serial_number || ''})`
      }));
      setEquipments(mappedEquipments);
    });
  }, []);

  useEffect(() => {
    const stateData = location.state as any;
    if (!stateData || !stateData.equipmentId) return;

    setSelectedEquipment([
      {
        id: stateData.equipmentId,
        code: `${stateData.equipmentDescription || ''} (SN ${
          stateData.equipmentSerialNumber || ''
        })`
      }
    ]);
  }, [location.state]);

  // EFFETTO DI INTERCETTAZIONE DELLA DUPLICAZIONE
  // Se la rotta si attiva a seguito del click su "Duplica", pre-compiliamo il form
  useEffect(() => {
    const stateData = location.state as any;

    if (!stateData?.isDuplicate || !stateData?.duplicateData) return;

    if (stateData?.isDuplicate && stateData?.duplicateData) {
      const source = stateData.duplicateData;

      setName(`${source.name || 'Checklist'} (Copia)`);
      setFrequency(source.frequency || 'daily');
      setDescription(source.description || '');
      setNotes(source.notes || '');

      if (Array.isArray(source.equipments)) {
        const formattedDuplicatedEquipments = source.equipments.map((eq: any) => ({
          id: eq.id,
          code: `${eq.description || ''} (SN ${eq.serial_number || ''})`
        }));
        setSelectedEquipment(formattedDuplicatedEquipments);
      } else if (source.equipment && source.equipment_deatils) {
        const eqDetails: IEquipmentSummary = source.equipment_deatils || source.equipment_details;
        if (eqDetails && typeof eqDetails === 'object') {
          setSelectedEquipment([
            {
              id: eqDetails.id || source.equipment,
              code: `${eqDetails.description || ''} (SN ${eqDetails.serial_number || ''})`
            }
          ]);
        }
      } else {
        setSelectedEquipment([]);
      }

      if (Array.isArray(source.sections)) {
        const duplicatedSectionsStructure: Section[] = source.sections.map((sec: any) => ({
          id: crypto.randomUUID(),
          title: sec.title || sec.name || '',
          period: sec.period || 'none',
          items: (sec.items || []).map((item: any) => ({
            id: crypto.randomUUID(),
            description: item.description || item.name || '',
            is_active: item.is_active ?? true
          }))
        }));
        setSections(duplicatedSectionsStructure);
      }
    }
  }, [location.state]);

  // ==================================================================
  //  LOGICA DI GESTIONE DELLE SEZIONI
  // ==================================================================
  const handleAddSection = () => {
    const newSection: Section = {
      id: crypto.randomUUID(),
      title: '',
      period: 'none',
      items: [{ id: crypto.randomUUID(), description: '' }]
    };
    setSections([...sections, newSection]);
  };

  const handleSectionPeriodChange = (
    sectionId: string,
    newPeriod: 'none' | 'morning' | 'afternoon'
  ) => {
    setSections((prev) =>
      prev.map((sec) => (sec.id === sectionId ? { ...sec, period: newPeriod } : sec))
    );
  };

  const handleSectionTitleChange = (sectionId: string, newTitle: string) => {
    setSections((prev) =>
      prev.map((sec) => (sec.id === sectionId ? { ...sec, title: newTitle } : sec))
    );
  };

  const handleRemoveSection = (sectionId: string) => {
    setSections((prev) => prev.filter((sec) => sec.id !== sectionId));
  };

  // ==================================================================
  //  LOGICA DI GESTIONE DELLE SINGOLE AZIONI
  // ==================================================================
  const handleAddItemToSection = (sectionId: string) => {
    setSections((prev) =>
      prev.map((sec) => {
        if (sec.id !== sectionId) return sec;
        return {
          ...sec,
          items: [...sec.items, { id: crypto.randomUUID(), description: '' }]
        };
      })
    );
  };

  const handleItemTextChange = (sectionId: string, itemId: string, text: string) => {
    setSections((prev) =>
      prev.map((sec) => {
        if (sec.id !== sectionId) return sec;
        return {
          ...sec,
          items: sec.items.map((it) => (it.id === itemId ? { ...it, description: text } : it))
        };
      })
    );
  };

  const handleRemoveItemFromSection = (sectionId: string, itemId: string) => {
    setSections((prev) =>
      prev.map((sec) => {
        if (sec.id !== sectionId) return sec;
        return {
          ...sec,
          items: sec.items.filter((it) => it.id !== itemId)
        };
      })
    );
  };

  const handleSave = async () => {
    if (!selectedEquipment || !name.trim() || sections.length === 0) return;

    setLoading(true);

    const formattedSections = sections
      .map((section, secIdx) => {
        const validItems = section.items
          .filter((it) => it.description.trim() !== '')
          .map((it, itemIdx) => ({
            sequence_number: itemIdx + 1,
            description: it.description.trim(),
            is_active: true
          }));

        return {
          title: section.title.trim() || `Sezione ${secIdx + 1}`,
          sequence_number: secIdx + 1,
          period: frequency === 'daily' ? section.period : 'none',
          items: validItems
        };
      })
      .filter((section) => section.items.length > 0);

    if (formattedSections.length === 0) {
      setLoading(false);
      return;
    }

    try {
      await equipmentCheckListTemplateService.add({
        equipments: selectedEquipment.map((eq) => Number(eq.id)),
        frequency,
        name: name.trim(),
        description: description.trim(),
        notes: notes.trim(),
        sections: formattedSections
      } as any);
      toast.success(t('actions.successfully.created') || 'Checklist creata con successo!');

      resetFormFields();
    } catch (err) {
      console.error('Errore durante la sottomissione della nuova checklist:', err);
      toast.error('Errore durante il salvataggio della scheda.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <TenantPage
      title={'Crea Nuova Checklist'}
      subtitle="Compila i campi sottostanti per generare una nuova scheda operativa riutilizzabile."
      menuRight={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="text"
            color="inherit"
            disabled={loading}
            onClick={() => window.history.back()}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={loading || !selectedEquipment || !name || sections.length === 0}
            onClick={handleSave}>
            Salva
          </Button>
        </Box>
      }>
      <Stack spacing={4}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Definisci le specifiche della scheda: aggiungi una sezione di raggruppamento e inserisci
            le singole voci.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome scheda checklist"
                placeholder="Es: Controllo Giornaliero Cappa"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                options={equipments}
                getOptionLabel={(option) => option.code || ''}
                value={selectedEquipment || []}
                onChange={(_, newValue) => setSelectedEquipment(newValue)}
                disabled={loading}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associa agli strumenti"
                    placeholder="Scegli le apparecchiature..."
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle1" color="primary" fontWeight={600} sx={{ mb: 2 }}>
            Configurazione di Default
          </Typography>

          <Stack spacing={2}>
            <TextField
              select
              label="Frequenza Predeterminata"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              SelectProps={{ native: true }}
              fullWidth
              disabled={loading}>
              {frequencyTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.code}
                </option>
              ))}
            </TextField>

            <TextField
              label="Descrizione / Obiettivi"
              placeholder="Fornisci dettagli o istruzioni per i tecnici..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
              disabled={loading}
            />

            <TextField
              label="Note interne aggiuntive"
              placeholder="Note libere..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
              disabled={loading}
            />
          </Stack>
        </Box>

        <Divider />

        <Stack>
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 2 }}>
              Struttura dei Compiti
            </Typography>

            {sections.length === 0 ? (
              <Box
                sx={{
                  py: 4,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  textAlign: 'center',
                  mb: 2
                }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Nessuna sezione inserita. Clicca sul pulsante sottostante per iniziare ad
                  aggiungere compiti.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={3} sx={{ mb: 3 }}>
                {sections.map((section, secIdx) => (
                  <Card
                    key={section.id}
                    variant="outlined"
                    sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.neutral' }}>
                    {/* Intestazione Sezione con Selettore del Turno Condizionale */}
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={frequency === 'daily' ? 7 : 11}>
                        <TextField
                          label={`Titolo Blocco / Area ${secIdx + 1}`}
                          placeholder="Es: Controlli di Sicurezza"
                          value={section.title}
                          onChange={(e) => handleSectionTitleChange(section.id, e.target.value)}
                          fullWidth
                          size="small"
                          disabled={loading}
                        />
                      </Grid>

                      {/* SELETTORE PERIODO: Visibile solo se la frequenza della scheda è daily */}
                      {frequency === 'daily' && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            label="Turno Competenza"
                            value={section.period || 'none'}
                            onChange={(e) =>
                              handleSectionPeriodChange(section.id, e.target.value as any)
                            }
                            SelectProps={{ native: true }}
                            fullWidth
                            size="small"
                            disabled={loading}>
                            <option value="none">Intera Giornata</option>
                            <option value="morning">Mattina </option>
                            <option value="afternoon">Pomeriggio </option>
                          </TextField>
                        </Grid>
                      )}

                      <Grid
                        item
                        xs={12}
                        sm={1}
                        sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title="Elimina intero blocco">
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveSection(section.id)}
                            size="small"
                            disabled={loading}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>

                    <Stack
                      spacing={1.5}
                      sx={{ pl: 3, borderLeft: '2px solid', borderColor: 'divider' }}>
                      {section.items.map((item, itemIdx) => (
                        <Box key={item.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            label={`Azione da eseguire ${itemIdx + 1}`}
                            placeholder="Es: Verificare pressione del gas"
                            value={item.description}
                            onChange={(e) =>
                              handleItemTextChange(section.id, item.id, e.target.value)
                            }
                            fullWidth
                            size="small"
                            disabled={loading}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddItemToSection(section.id);
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => handleRemoveItemFromSection(section.id, item.id)}
                            disabled={loading || section.items.length <= 1}>
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      ))}

                      <Button
                        variant="text"
                        size="small"
                        startIcon={<PlaylistAddIcon />}
                        disabled={loading}
                        onClick={() => handleAddItemToSection(section.id)}
                        sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                        Aggiungi voce
                      </Button>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}

            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={handleAddSection}
              disabled={loading}
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.95rem',
                '&:hover': { bgcolor: 'action.hover' },
                mt: 1
              }}>
              Aggiungi sezione
            </Button>
          </Box>
        </Stack>
      </Stack>
    </TenantPage>
  );
};

export default CreateChecklist;
